import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Alert, Tabs, Tab } from 'react-bootstrap';
import { Save } from 'lucide-react';
import type {
  AgentPersonaResponse,
  UpsertAgentPersonaRequest,
} from '../../../infrastructure/AIAgentRegistryClient';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import { MarkdownEditor } from '../../../components/common/MarkdownEditor';

const STORAGE_KEY_PREFIX = 'agent-persona-draft-';

interface UpsertAgentModalProps {
  show: boolean;
  agent: AgentPersonaResponse | null;
  type: 'supervisor' | 'micro';
  parentId?: string | undefined;
  onHide: () => void;
  onSubmit: (data: UpsertAgentPersonaRequest) => void;
  isSubmitting: boolean;
}

export const UpsertAgentModal = ({
  show,
  agent,
  type,
  parentId,
  onHide,
  onSubmit,
  isSubmitting,
}: UpsertAgentModalProps) => {
  const { categoriesQuery } = useAgentRegistry();
  const { data: categories = [] } = categoriesQuery;
  const [activeTab, setActiveTab] = useState<'basic' | 'persona'>('basic');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [manualId, setManualId] = useState('');
  const [requiredParams, setRequiredParams] = useState('');
  const [personaMarkdown, setPersonaMarkdown] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!agent && !!agent.id;
  const storageKey = agent?.id ? `${STORAGE_KEY_PREFIX}${agent.id}` : null;

  useEffect(() => {
    if (agent) {
      setName(agent.name);
      setCategory(agent.category || '');
      setDescription(agent.description);
      setManualId(agent.manualId || '');
      setRequiredParams(agent.requiredParams ? JSON.stringify(agent.requiredParams, null, 2) : '');
      if (agent.id) {
        const draftKey = `${STORAGE_KEY_PREFIX}${agent.id}`;
        const savedDraft = localStorage.getItem(draftKey);
        if (savedDraft) {
          setPersonaMarkdown(savedDraft);
          setHasDraft(true);
        } else {
          setPersonaMarkdown(agent.personaMarkdown || '');
          setHasDraft(false);
        }
      } else {
        setPersonaMarkdown(agent.personaMarkdown || '');
        setHasDraft(false);
      }
    } else {
      setName('');
      setCategory('');
      setDescription('');
      setManualId('');
      setRequiredParams('');
      setPersonaMarkdown('');
      setHasDraft(false);
    }
    setActiveTab('basic');
    setErrors({});
  }, [agent, show]);

  useEffect(() => {
    if (!category && categories.length > 0 && categories[0]) {
      setCategory(categories[0].name);
    }
  }, [categories, category]);

  const saveDraft = useCallback((content: string) => {
    if (!storageKey) return;
    if (content.trim()) {
      localStorage.setItem(storageKey, content);
      setHasDraft(true);
    } else {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
    }
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    localStorage.removeItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  const discardDraft = useCallback(() => {
    if (!storageKey || !agent) return;
    localStorage.removeItem(storageKey);
    setPersonaMarkdown(agent.personaMarkdown || '');
    setHasDraft(false);
  }, [storageKey, agent]);

  const handleMarkdownChange = useCallback((value: string) => {
    setPersonaMarkdown(value);
    saveDraft(value);
  }, [saveDraft]);


  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors['name'] = 'Name is required';
    if (!category.trim()) newErrors['category'] = 'Category is required';
    if (!description.trim()) newErrors['description'] = 'Description is required';
    if (!personaMarkdown.trim()) newErrors['personaMarkdown'] = 'Persona markdown is required';
    if (requiredParams.trim()) {
      try {
        JSON.parse(requiredParams);
      } catch {
        newErrors['requiredParams'] = 'Invalid JSON format';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data: UpsertAgentPersonaRequest = {
      name,
      type,
      category,
      description,
      personaMarkdown: personaMarkdown || '',
    };

    if (isEdit && agent) data.id = agent.id;
    if (type === 'micro' && parentId) data.parentId = parentId;
    if (manualId.trim()) data.manualId = manualId;
    if (requiredParams.trim()) data.requiredParams = JSON.parse(requiredParams);

    clearDraft();
    onSubmit(data);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-80w">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit' : 'Create'} {type === 'supervisor' ? 'Supervisor' : 'Micro'} Agent</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'basic' | 'persona')} className="mb-3">
            <Tab eventKey="basic" title="Basic Info">
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., OrderCancelAgent"
              isInvalid={!!errors['name']}
            />
            <Form.Control.Feedback type="invalid">{errors['name']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category *</Form.Label>
            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} isInvalid={!!errors['category']}>
              {categories.length === 0 ? (
                <option>Loading categories...</option>
              ) : (
                categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                  </option>
                ))
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors['category']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this agent does..."
              isInvalid={!!errors['description']}
            />
            <Form.Control.Feedback type="invalid">{errors['description']}</Form.Control.Feedback>
          </Form.Group>

          {type === 'micro' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Linked Manual ID (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="Enter manual ID"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Required Parameters (JSON Schema, optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={requiredParams}
                  onChange={(e) => setRequiredParams(e.target.value)}
                  placeholder='{"type": "object", "required": ["orderId"], "properties": {...}}'
                  isInvalid={!!errors['requiredParams']}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <Form.Control.Feedback type="invalid">{errors['requiredParams']}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          {type === 'micro' && parentId && (
            <Alert variant="info">This micro agent will be created under supervisor: {parentId}</Alert>
          )}
            </Tab>
            <Tab eventKey="persona" title={<span>Persona Markdown {hasDraft && <Save size={14} className="ms-1 text-warning" />}</span>}>
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Persona Markdown</Form.Label>
                  {hasDraft && (
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-warning">
                        <Save size={12} className="me-1" />Draft saved
                      </small>
                      <Button variant="outline-secondary" size="sm" onClick={discardDraft}>
                        Discard Draft
                      </Button>
                    </div>
                  )}
                </div>
                <Form.Text className="text-muted d-block mb-2">
                  Define the persona instructions and behavior for this agent using markdown. Changes are auto-saved locally.
                </Form.Text>
                <MarkdownEditor
                  value={personaMarkdown}
                  onChange={handleMarkdownChange}
                  height={600}
                />
              </Form.Group>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

